import {
  clampWorkers,
  localDate,
  maxWorkers,
  versionId,
} from './buildJobInput';
import type { JobSpec, JobValues } from './types';
/**
 * Train the perception models for one universe.
 *
 * The script is a tryCatchLog wrapper around main_brms.R. It writes its own
 * `gastrograph.perception_model_job` row keyed on the version id we send —
 * which is the only thread connecting what was launched here to what appears in
 * the history table — and on success it calls `deploy_perception_model_job`, so
 * a finished run silently becomes production for its universe. Hence the
 * confirmation.
 *
 * All four flags are required twice over: by the script's own `stopifnot` and
 * again by conchBatch, which 400s a missing one before a container starts.
 */

export const TRAIN_PERCEPTION_MODELS_SCRIPT =
  'batchjobs/trigger_1_train_perception_models.R';

export const DEFAULT_QUEUE = 'gg-queue-efs-8xlarge';

/**
 * The universes that have their own model. Not read from
 * perception_model_job: only trained universes appear there, which would make
 * the first run of a universe unreachable from the form that starts it.
 */
export const UNIVERSES = ['food', 'nonfood'] as const;

export const trainPerceptionModels: JobSpec = {
  key: 'trainPerceptionModels',
  title: 'Train perception models',
  description:
    'Fits the ggvar models for one universe against every review up to the pull date, then publishes the result as the live model for that universe.',
  scriptPath: TRAIN_PERCEPTION_MODELS_SCRIPT,
  queueField: 'hardware',

  fields: [
    {
      name: 'pull_date',
      label: 'Pull date',
      type: 'date',
      noLaterThanToday: true,
      help: 'Reviews after this date are left out. Anything later than today trains the same set as today.',
    },
    {
      name: 'universe',
      label: 'Universe',
      type: 'select',
      options: UNIVERSES,
    },
    {
      name: 'hardware',
      label: 'Hardware',
      type: 'select',
      advanced: true,
      // gg-queue-efs-16xlarge is deliberately absent: conchBatch does not map
      // it, so the container override falls back to 8 vCPU and the run crawls.
      options: ['gg-queue-efs-8xlarge', 'gg-queue-efs-4xlarge'],
      help: 'Selects the compute environment. Larger queues fit more models at once.',
    },
    {
      name: 'workers',
      label: 'Workers',
      type: 'number',
      advanced: true,
      min: 1,
      max: (values: JobValues) => maxWorkers(String(values.hardware)),
      help: 'One fit per worker, capped one below the queue’s cores so the parent process keeps one.',
    },
  ],

  buildArgs: (values: JobValues, nonce: string) => {
    const pullDate = String(values.pull_date);
    return {
      version_id: versionId(pullDate, nonce),
      universe: String(values.universe),
      pull_date: pullDate,
      workers: clampWorkers(Number(values.workers), String(values.hardware)),
    };
  },

  confirm: {
    title: 'Start this run?',
  },
};

/** Sensible starting point for the form. */
export const defaultValues = (): JobValues => ({
  pull_date: localDate(),
  universe: UNIVERSES[0],
  hardware: DEFAULT_QUEUE,
  workers: maxWorkers(DEFAULT_QUEUE),
});
